import * as React from 'react';
import { Button, Form, ListGroup } from 'react-bootstrap';
import {
  getGmailMessages, getGmailMessagesByCompany, getGmailThread, getGmailStatus, sendReply
} from 'api/gmailApi';
import { GmailMessage } from 'Interfaces/EmailInterface';
import { getLocalization } from 'global/global';
import { useAppSelector } from 'index';

interface Props {
  contactEmail: string;
  companyId?: string;
  crmFormType?: string;
  sent: boolean;
  resetMessageSent: () => void;
}

export default function GmailThreadView({ contactEmail, companyId, sent, crmFormType, resetMessageSent }: Props) {

  const clientPersist = useAppSelector(state => state.clientPersist);
  const [emailView, setEmailView] = React.useState<'threads' | 'thread'>('threads');
  const [emailThreads, setEmailThreads] = React.useState<GmailMessage[]>([]);
  const [activeThread, setActiveThread] = React.useState<GmailMessage[]>([]);
  const [expandedMessages, setExpandedMessages] = React.useState<Set<string>>(new Set());
  const [threadsLoading, setThreadsLoading] = React.useState(false);
  const [gmailConnected, setGmailConnected] = React.useState<boolean | null>(null);
  const [replyOpen, setReplyOpen] = React.useState(false);
  const [replyBody, setReplyBody] = React.useState('');
  const [replySending, setReplySending] = React.useState(false);

  const fetchMessages = () => {
    if (crmFormType === 'deal' && companyId) {
      return getGmailMessagesByCompany(companyId);
    } else if (contactEmail) {
      return getGmailMessages(contactEmail);
    }
    return null;
  };
  const initGmail = () => {
    setThreadsLoading(true);
    getGmailStatus()
      .then(res => {
        if (res.status === 'OK') {
          setGmailConnected(true);
          if ((crmFormType === 'deal' && companyId) || (crmFormType === 'person' && contactEmail)) {
            return fetchMessages()?.then(msgs => setEmailThreads(msgs))
              .catch(() => setEmailThreads([]));
          }
        } else {
          setGmailConnected(false);
        }
        return;
      })
      .catch(() => setGmailConnected(false))
      .finally(() => setThreadsLoading(false));
  };

  React.useEffect(() => {
    initGmail();
  }, []);

  React.useEffect(() => {
    if (clientPersist.gmailConnection) {
      initGmail();
    }
  }, [clientPersist.gmailConnection]);

  React.useEffect(() => {
    if (sent) {
      fetchMessages()?.then(msgs => setEmailThreads(msgs))
        .catch(() => setEmailThreads([]));
      resetMessageSent();
    }
  }, [sent]);

  const threadPreviews = React.useMemo(() => {
    const map = new Map<string, GmailMessage>();
    for (const msg of emailThreads) {
      const existing = map.get(msg.threadId);
      if (!existing || new Date(msg.date) > new Date(existing.date)) {
        map.set(msg.threadId, msg);
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [emailThreads]);

  const handleReply = () => {
    const last = activeThread[activeThread.length - 1];
    if (!last || !replyBody.trim()) {
      return;
    }
    const baseSubject = last.subject || '';
    setReplySending(true);
    sendReply({
      threadId: last.threadId,
      messageId: last.messageId || '',
      to: last.from.indexOf(contactEmail) > -1 ? last.from : contactEmail,
      subject: baseSubject.startsWith('Re:') ? baseSubject : `Re: ${baseSubject}`,
      bodyText: replyBody,
    }).then(json => {
      if (json.status === 'OK') {
        setReplyOpen(false);
        setReplyBody('');
        return getGmailThread(last.threadId, last.userId).then(msgs => {
          setActiveThread(msgs);
          if (msgs.length > 0) {
            setExpandedMessages(new Set([msgs[msgs.length - 1].id]));
          }
        });
      }
    })
      .catch(e => console.error(e))
      .finally(() => setReplySending(false));
  };

  const openThread = (threadId: string, userId: number) => {
    void getGmailThread(threadId, userId).then(msgs => {
      setActiveThread(msgs);
      if (msgs.length > 0) {
        setExpandedMessages(new Set([msgs[msgs.length - 1].id]));
      }
      setEmailView('thread');
    });
  };

  const toggleMessage = (id: string) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });

  if (threadsLoading) {
    return <div className="text-muted small p-2" data-testid="loading">{getLocalization('loading')}</div>;
  }

  if (gmailConnected === false) {
    return (
      <div className="d-flex flex-column align-items-center py-3 gap-2">
        <div
          className="small text-muted"
          data-testid="not-connected-div"
        >
          {getLocalization('emailNotConnected')}
        </div>
        <Button
          size="sm"
          data-testid="connect-email-btn"
          variant="outline-primary"
          href={`/json/gmail/start?userid=${
            encodeURIComponent(clientPersist.userName || '')
          }&key=${encodeURIComponent(clientPersist.key || '')}`}
          target="_blank"
        >
          {getLocalization('connectEmail')}
        </Button>
      </div>
    );
  }

  if (emailView === 'threads') {
    if (threadPreviews.length === 0) {
      return (
        <div
          className="text-muted small p-2"
          data-testid="not-messages-div"
        >{getLocalization('noMessages')}</div>
      );
    }
    return (
      <ListGroup variant="flush">
        {threadPreviews.map(thread => (
          <ListGroup.Item
            key={thread.threadId}
            action
            className="px-0 py-2"
            onClick={() => openThread(thread.threadId, thread.userId)}
          >
            <div className="d-flex justify-content-between">
              <span className="fw-semibold small text-truncate">{thread.from}</span>
              <small className="text-muted text-nowrap ms-2">
                {formatDate(new Date(thread.date).getTime())}
              </small>
            </div>
            <div className="small text-truncate">{thread.subject}</div>
          </ListGroup.Item>
        ))}
      </ListGroup>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center mb-2 gap-2">
        <Button
          size="sm"
          variant="link"
          className="p-0"
          onClick={() => setEmailView('threads')}
          data-testid="back-btn"
        >
          ← {getLocalization('back')}
        </Button>
        <span className="fw-semibold small text-truncate">{activeThread[0]?.subject}</span>
        {crmFormType === 'person' && (
          <Button
            size="sm"
            variant="outline-primary"
            className="ms-auto"
            data-testid="reply-btn"
            onClick={() => setReplyOpen(v => !v)}
          >
            {getLocalization('reply')}
          </Button>
        )}
      </div>
      <ListGroup variant="flush">
        {activeThread.map((msg, idx) => {
          const isLast = idx === activeThread.length - 1;
          const isExpanded = isLast || expandedMessages.has(msg.id);
          return (
            <ListGroup.Item
              key={msg.id}
              className="px-0 py-2"
              action={!isLast}
              onClick={!isLast ? () => toggleMessage(msg.id) : undefined}
            >
              <div className="d-flex justify-content-between align-items-start">
                <span className="fw-semibold small">{msg.from}</span>
                <small className="text-muted text-nowrap ms-2">
                  {formatDate(new Date(msg.date).getTime())}
                </small>
              </div>
              {!isExpanded ? (
                <div className="small text-muted text-truncate">{msg.snippet}</div>
              ) : msg.bodyHtml ? (
                <div
                  className="small mt-1 crm-email-body"
                  dangerouslySetInnerHTML={{ __html: msg.bodyHtml }}
                />
              ) : (
                <div className="small mt-1 crm-email-body">{msg.bodyText}</div>
              )}
            </ListGroup.Item>
          );
        })}
      </ListGroup>
      {crmFormType === 'person' && replyOpen && (
        <div className="crm-reply-composer mt-2" data-testid="reply-composer">
          <Form.Group className="mb-2">
            <Form.Control
              as="textarea"
              rows={4}
              size="sm"
              placeholder={getLocalization('reply')}
              value={replyBody}
              disabled={replySending}
              data-testid="reply-body-input"
              onChange={e => setReplyBody(e.target.value)}
            />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button
              size="sm"
              variant="link"
              disabled={replySending}
              data-testid="reply-cancel-btn"
              onClick={() => { setReplyOpen(false); setReplyBody(''); }}
            >
              {getLocalization('cancel')}
            </Button>
            <Button
              size="sm"
              disabled={replySending || !replyBody.trim()}
              data-testid="reply-send-btn"
              onClick={handleReply}
            >
              {getLocalization('send')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
